import { plainToClass } from "class-transformer";
import { LMSSiteStudyTrainingUserDto, LMSUserTrainingDetailDto, studyIdParam } from "./trainings.objects";
import { MultiFileUploadManager } from "./multi-file-upload-manager";

export class LMSSiteStudyTrainingDetailsPage {
    userTrainingsTable: any;
    materialsUploadManager: MultiFileUploadManager;
    private editingTrainingId: number | null = null;
    private editingTraining: LMSUserTrainingDetailDto | null = null;
    private checkedUserTrainingIds: Set<number> = new Set();

    constructor(private siteId: number, private studyId: number | null, private trainingId: number) {
        this.loadData();
        this.initializeTable();
        this.initializeDialog();
        this.initializeBulkEdit();
    }

    private loadData(): void {
        $.get(`?handler=UserTrainings&siteId=${this.siteId}${studyIdParam(this.studyId)}&trainingId=${this.trainingId}`)
            .done((result: LMSSiteStudyTrainingUserDto[]) => {
                const rows = plainToClass(LMSSiteStudyTrainingUserDto, result ?? []);
                this.userTrainingsTable.clear();
                this.userTrainingsTable.rows.add(rows).draw();
            });
    }

    private initializeTable(): void {
        this.userTrainingsTable = $('#lmsTrainingUserTrainingsTable').DataTable({
            dom: 'rtip',
            autoWidth: false,
            info: false,
            paging: false,
            search: true,
            scrollX: true,
            scrollY: '60vh',
            scrollCollapse: true,
            order: [[0, 'asc']],
            columns: [
                { data: "userName", title: "Name", className: "user-name text-nowrap" },
                { data: "roles", title: "Role" },
                { data: "completedAt", title: "Completed Date" },
                { data: "expirationDate", title: "Expiration Date" },
                { data: "status", title: "Status" },
                {
                    data: null,
                    title: '<input type="checkbox" id="bulk-select-all" class="completion-check" />',
                    className: "text-center lms-table-col-actions",
                    orderable: false,
                    render: function (data: any, type: any, row: LMSSiteStudyTrainingUserDto) {
                        const isComplete = row.status === "Complete";
                        return `
                            <button class="lms-edit-training"><i class="fa-solid fa-pencil"></i></button>
                            <input class="completion-check bulk-row-check mb-0" type="checkbox" data-id="${row.userTrainingId}" ${isComplete ? 'disabled' : ''}/>
                        `;
                    }
                }
            ],
            data: []
        });

        $("#lmsTrainingUserTrainingsTable tbody").on('click', '.lms-edit-training', (e) => {
            var row = $(e.currentTarget).closest('tr');
            var data = $("#lmsTrainingUserTrainingsTable").DataTable().row(row).data();
            if (!data || !data.userTrainingId) return;

            this.openEditDialog(data.userTrainingId);
        })
    }

    private initializeDialog(): void {
        this.materialsUploadManager = new MultiFileUploadManager("#files-input", "#files-list");

        document.getElementById("files-add-btn").addEventListener("click", () => {
            document.getElementById("files-input").click();
        });

        $('#userTrainingFormDialog').on('hidden.bs.modal', () => {
            this.materialsUploadManager.reset();
            $("#completeTrainingPassword").val("");
            this.editingTraining = null;
        })

        $("#complete-training-checkbox").on('change', () => {
            this.autoPopulateExpirationDate();
            this.updateCompletePasswordVisibility();
        });

        $("#saveUserTrainingBtn").on('click', () => this.submitUserTrainingForm());

        // See site-study-user-details.page.ts: Enter in the password box would otherwise
        // natively GET-submit this form and wipe the page's query string.
        $('#userTrainingForm').on('submit', (e) => {
            e.preventDefault();
            this.submitUserTrainingForm();
        });
    }

    private initializeBulkEdit(): void {
        $('#bulk-edit-toggle').on('change', (e) => {
            const enabled = (e.target as HTMLInputElement).checked;
            $('.lms-training-table').toggleClass('bulk-edit-mode', enabled);
            $('#bulk-complete-btn').toggleClass('d-none', !enabled);
            $(".lms-edit-training").toggleClass('d-none', enabled);

            if (!enabled) {
                this.checkedUserTrainingIds.clear();
                $('.bulk-row-check').prop('checked', false);
                this.updateBulkCompleteState();
            }
        });

        $('#lmsTrainingUserTrainingsTable').on('change', '.bulk-row-check', (e) => {
            const id = Number($(e.currentTarget).data('id'));
            if ((e.target as HTMLInputElement).checked) {
                this.checkedUserTrainingIds.add(id);
            } else {
                this.checkedUserTrainingIds.delete(id);
            }
            this.updateBulkCompleteState();
        });

        $('#bulk-select-all').on('change', (e) => {
            const checked = (e.target as HTMLInputElement).checked;

            this.userTrainingsTable.rows().every((rowIdx: number) => {
                const rowData = this.userTrainingsTable.row(rowIdx).data();
                if (rowData.status === "Complete") {
                    return;
                }

                if (checked) {
                    this.checkedUserTrainingIds.add(rowData.userTrainingId);
                } else {
                    this.checkedUserTrainingIds.delete(rowData.userTrainingId);
                }
            });

            $('.bulk-row-check:not(:disabled)').prop('checked', checked);
            this.updateBulkCompleteState();
        });

        this.userTrainingsTable.on('draw', () => {
            $('.bulk-row-check').each((_: number, el: HTMLElement) => {
                const id = Number($(el).data('id'));
                $(el).prop('checked', this.checkedUserTrainingIds.has(id));
            });
            this.updateSelectAllState();
        });

        $('#bulk-complete-btn').on('click', () => this.openBulkCompleteDialog());
        $('#saveBulkCompleteBtn').on('click', () => this.submitBulkCompleteForm());
    }

    private updateBulkCompleteState(): void {
        $('#bulk-complete-btn').prop('disabled', this.checkedUserTrainingIds.size === 0);
        this.updateSelectAllState();
    }

    private updateSelectAllState(): void {
        const rowChecks = $('.bulk-row-check:not(:disabled)');
        const checkedCount = rowChecks.filter(':checked').length;
        const selectAll = $('#bulk-select-all') as JQuery<HTMLInputElement>;

        selectAll.prop('checked', rowChecks.length > 0 && checkedCount === rowChecks.length);
        selectAll.prop('indeterminate', checkedCount > 0 && checkedCount < rowChecks.length);
    }

    private openBulkCompleteDialog(): void {
        $('#bulkAttestCompletionCheckbox').prop('checked', false);
        $('#bulkCompleteTrainingPassword').val('');
        $('#bulkCompleteTrainingDialog .is-invalid').removeClass('is-invalid');
        $('#bulkCompleteTrainingDialog .invalid-feedback').text('').hide();
        $('#bulkCompleteTrainingDialog').modal('show');
    }

    private submitBulkCompleteForm(): void {
        if (!this.validateBulkComplete()) {
            return;
        }

        $('#bulkCompleteSpinner').show();

        $.ajax({
            url: `?handler=MarkTrainingsComplete&trainingId=${this.trainingId}`,
            method: 'POST',
            traditional: true,
            headers: { 'RequestVerificationToken': $('input[name="__RequestVerificationToken"]').val() as string },
            data: {
                ids: Array.from(this.checkedUserTrainingIds),
                password: $('#bulkCompleteTrainingPassword').val()
            }
        }).done(() => {
            $('#bulkCompleteSpinner').hide();
            $('#bulkCompleteTrainingDialog').modal('hide');
            this.checkedUserTrainingIds.clear();
            $('#bulk-edit-toggle').prop('checked', false).trigger('change');
            this.loadData();
        }).fail((e) => {
            $('#bulkCompleteSpinner').hide();
            this.markInvalid(
                $('#bulkCompleteTrainingPassword'),
                e.responseJSON?.message ?? "Unable to verify your password."
            );
        });
    }

    private validateBulkComplete(): boolean {
        $('#bulkCompleteTrainingDialog .is-invalid').removeClass('is-invalid');
        $('#bulkCompleteTrainingDialog .invalid-feedback').text('').hide();

        let isValid = true;

        const attestEl = $('#bulkAttestCompletionCheckbox');
        if (!attestEl.is(':checked')) {
            this.markInvalid(attestEl, "Please agree to the statement to mark these trainings as complete.");
            isValid = false;
        }

        const passEl = $('#bulkCompleteTrainingPassword');
        if (!passEl.val()) {
            this.markInvalid(passEl, "Please enter your password to verify your identity.");
            isValid = false;
        }

        return isValid;
    }

    private openEditDialog(trainingId: number) {
        $("#userTrainingFormDialog .is-invalid").removeClass("is-invalid");
        $(".invalid-feedback").text("").hide();

        this.editingTrainingId = trainingId;
        $.get(`?handler=UserTrainingDetails&id=${trainingId}`)
            .done((training: LMSUserTrainingDetailDto) => {
                this.editingTraining = training;
                $("#userTrainingDialogTitle").text(training.trainingName).show();
                $("#files-container").toggle(training.requiresUpload);
                this.populateForm(training);
                this.updateCompletePasswordVisibility();
                $('#userTrainingFormDialog').modal('show');
            })
        //.fail(() => this.showServerError());
    }

    private populateForm(t: LMSUserTrainingDetailDto) {
        if (t.requiresUpload) {
            this.materialsUploadManager.setExistingMaterials(t.uploads);
        }

        $("#dueDate").val(t.dueDate?.substring(0, 10));
        $("#expirationDate")
            .prop("disabled", !t.canExpire)
            .val(t.canExpire ? t.expirationDate?.substring(0, 10) : "");
        $("#notes").val(t.notes);
        $('#complete-training-checkbox').prop('checked', t.isComplete);
    }

    private autoPopulateExpirationDate(): void {
        const isChecked = $("#complete-training-checkbox").prop("checked");
        const expirationDateInput = $("#expirationDate");

        if (isChecked && this.editingTraining?.canExpire && !expirationDateInput.val()) {
            const expiration = new Date();
            expiration.setDate(expiration.getDate() + this.editingTraining.defaultExpirationTimeInDays);
            expirationDateInput.val(expiration.toISOString().substring(0, 10));
        }
    }

    // Password confirmation is only needed when this action is the training isn't it's already complete.
    private updateCompletePasswordVisibility(): void {
        const isNewlyCompleted = !this.editingTraining?.isComplete && $("#complete-training-checkbox").prop("checked");
        $("#complete-password-container").prop("hidden", !isNewlyCompleted);
        if (!isNewlyCompleted) {
            $("#completeTrainingPassword").val("");
        }
    }

    private submitUserTrainingForm() {
        if (!this.validate()) {
            return;
        }

        $("#saveTrainingSpinner").show();

        const isComplete = $("#complete-training-checkbox").prop("checked");
        const isNewlyCompleted = !this.editingTraining?.isComplete && isComplete;

        if (isNewlyCompleted) {
            this.markTrainingComplete()
                .done(() => this.saveTrainingDates())
                .fail((e) => {
                    $("#saveTrainingSpinner").hide();
                    this.markInvalid(
                        $("#completeTrainingPassword"),
                        e.responseJSON?.message ?? "Unable to verify your password."
                    );
                });
        } else {
            this.saveTrainingDates();
        }
    }

    private markTrainingComplete() {
        return $.ajax({
            url: `?handler=MarkComplete&id=${this.editingTrainingId}`,
            method: 'POST',
            headers: { 'RequestVerificationToken': $('input[name="__RequestVerificationToken"]').val() as string },
            data: {
                password: $("#completeTrainingPassword").val()
            }
        });
    }

    private saveTrainingDates() {
        const payload = this.buildPayload(this.editingTrainingId);

        $.ajax({
            url: `?handler=UpdateUserTraining`,
            method: 'POST',
            contentType: false,
            processData: false,
            headers: { 'RequestVerificationToken': $('input[name="__RequestVerificationToken"]').val() as string },
            data: payload
        }).done(() => {
            $("#saveTrainingSpinner").hide();
            $('#userTrainingFormDialog').modal('hide');
            this.loadData();
        }).fail((e) => {
            $("#saveTrainingSpinner").hide();
            console.log(e);
        });
    }

    private validate(): boolean {
        $("#userTrainingFormDialog .is-invalid").removeClass("is-invalid");
        $(".invalid-feedback").text("").hide();

        let isValid = true;

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const expEl = $("#expirationDate");
        const expVal = expEl.val() as string;
        const isComplete = $("#complete-training-checkbox").prop("checked");
        if (isComplete && this.editingTraining?.canExpire) {
            if (!expVal) {
                this.markInvalid(expEl, "Expiration date is required.");
                isValid = false;
            } else if (expVal && new Date(expVal) < today) {
                this.markInvalid(expEl, "Expiration date must be on or after today.");
                isValid = false;
            }
        }

        const dueEl = $("#dueDate");
        const dueVal = dueEl.val() as string;
        if (!dueVal) {
            this.markInvalid(dueEl, "Due date is required.");
            isValid = false;
        }
        //else if (!isComplete && new Date(dueVal) < today) {
        //    this.markInvalid(dueEl, "Due date must be on or after today.");
        //    isValid = false;
        //}

        const fileEl = $("#files-list");
        if (this.editingTraining?.requiresUpload && isComplete && fileEl.children().length < 1) {
            this.markInvalid(fileEl, "At least one file is required.");
            isValid = false;
        }

        const passEl = $("#completeTrainingPassword");
        const isNewlyCompleted = !this.editingTraining?.isComplete && isComplete;
        if (isNewlyCompleted && !passEl.val()) {
            this.markInvalid(passEl, "Please enter your password to verify your identity.");
            isValid = false;
        }

        return isValid;
    }

    private markInvalid(element: JQuery, errorMessage: string) {
        const formGroup = element.closest('.form-group');

        if (element.is(":checkbox")) {
            formGroup.find(".lms-form-check").addClass("is-invalid");
        } else {
            element.addClass("is-invalid");
        }

        formGroup.find(".invalid-feedback").text(errorMessage).show();
    }

    private buildPayload(id: number): FormData {
        const formData = new FormData();

        formData.append("Id", id.toString());
        formData.append("DueDate", $("#dueDate").val() as string);

        const expVal = $("#expirationDate").val() as string;
        if (expVal) {
            formData.append("ExpirationDate", expVal);
        }

        formData.append("IsComplete", $("#complete-training-checkbox").prop("checked"));

        this.materialsUploadManager.getFiles().forEach(file => {
            formData.append("Materials", file, file.name);
        })

        this.materialsUploadManager.getRemovedMaterialIds().forEach(id => {
            formData.append("RemovedMaterialIds", id.toString());
        })

        return formData;
    }
}
